import { useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { AntdConfigProvider } from '@/contexts/AntdConfigProvider';
import { Toaster } from '@/components/ui/toaster';
import { PermissionDeniedModal } from '@/components/PermissionDeniedModal';
import { RequestErrorNotifier } from '@/components/RequestErrorNotifier';
import { useAuthStore } from '@/store/auth.store';
import Layout from '@/components/Layout/index';
import OidcCallback from '@/pages/auth/OidcCallback';
import { isOidcTokenValid, redirectToOidcLogin } from '@/pages/auth/oidc';
import FeishuCallback from '@/pages/FeishuCallback';
import Dashboard from '@/pages/common/Dashboard';
import Agents from '@/pages/agent/Agents';
import AgentOverview from '@/pages/agent/AgentOverview';
import AgentApps from '@/pages/agent/AgentApps';
import AgentAppDetail from '@/pages/agent/AgentAppDetail';
import AgentTrace from '@/pages/agent/AgentTrace';
import AgentStats from '@/pages/agent/AgentStats';
import CreateAgent from '@/pages/agent/CreateAgent';
import EditAgent from '@/pages/agent/EditAgent';
import CreateAgentInstance from '@/pages/agent/CreateAgentInstance';
import Users from '@/pages/common/Users';
import MCPs from '@/pages/mcp/MCPs';
import MCPClassDetail from '@/pages/mcp/MCPClassDetail';
import MCPInstanceDetail from '@/pages/mcp/MCPInstanceDetail';
import EditMCPInstance from '@/pages/mcp/EditMCPInstance';
import CreateMCPClass from '@/pages/mcp/CreateMCPClass';
import EditMCPClass from '@/pages/mcp/EditMCPClass';
import CreateMCPInstance from '@/pages/mcp/CreateMCPInstance';
import CreateMCPInstanceFromClass from '@/pages/mcp/CreateMCPInstanceFromClass';
import MemoryCenter from '@/pages/memory/MemoryCenter';
import MemoryClassDetail from '@/pages/memory/MemoryClassDetail';
import RAGs from '@/pages/rag/RAGs';
import RAGCatalog from '@/pages/rag/RAGCatalog';
import RAGClassDetail from '@/pages/rag/RAGClassDetail';
import RAGDetail from '@/pages/rag/RAGDetail';
import Sandboxes from '@/pages/sandbox/Sandboxes';
import CreateSandboxClass from '@/pages/sandbox/CreateSandboxClass';
import EditSandboxClass from '@/pages/sandbox/EditSandboxClass';
import CreateSandboxInstance from '@/pages/sandbox/CreateSandboxInstance';
import EditSandboxInstance from '@/pages/sandbox/EditSandboxInstance';
import CreateSandboxInstanceFromClass from '@/pages/sandbox/CreateSandboxInstanceFromClass';
import SandboxInstanceDetail from '@/pages/sandbox/SandboxInstanceDetail';
import SandboxClassDetail from '@/pages/sandbox/SandboxClassDetail';
import Skills from '@/pages/skill/Skills';
import SkillGroupClassDetail from '@/pages/skill/SkillGroupClassDetail';
import SkillGroupDetail from '@/pages/skill/SkillGroupDetail';
import SkillsManagement from '@/pages/skill/SkillsManagement';
import SkillManagementDetail from '@/pages/skill/SkillManagementDetail';
import SkillsPlaza from '@/pages/skill/SkillsPlaza';
import SkillsPlazaDetail from '@/pages/skill/SkillsPlazaDetail';
import Applications from '@/pages/application/Applications';
import CreateApplication from '@/pages/application/CreateApplication';
import EditApplication from '@/pages/application/EditApplication';
import Channels from '@/pages/channel/Channels';
import ChannelAccounts from '@/pages/channel/ChannelAccounts';
import ChannelAccountDetailRouter from '@/pages/channel/ChannelAccountDetailRouter';
import ChannelSessions from '@/pages/channel/ChannelSessions';
import Settings from '@/pages/common/Settings';
import Models from '@/pages/common/Models';
import Credentials from '@/pages/credentials';
import IamUsersRoutes from '@/pages/iam/users';
import IamGroupsRoutes from '@/pages/iam/groups';
import IamPoliciesRoutes from '@/pages/iam/policies';
import IamGrantsRoutes from '@/pages/iam/grants';

function AuthGuard({ children }: { children: React.ReactNode }) {
  const token = useAuthStore((state) => state.token);
  if (!isOidcTokenValid(token)) {
    return <OidcLoginRedirect />;
  }
  return <>{children}</>;
}

function OidcLoginRedirect() {
  const logout = useAuthStore((state) => state.logout);
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) {
      return;
    }

    startedRef.current = true;
    logout();
    redirectToOidcLogin();
  }, [logout]);

  return null;
}

function LegacySkillClassDetailRedirect() {
  const { id = '' } = useParams<{ id: string }>();
  return <Navigate to={`/plugins/skills/group-classes/${id}`} replace />;
}

function App() {
  const { token, _hasHydrated } = useAuthStore();

  if (!_hasHydrated) {
    return null;
  }

  const hasValidToken = isOidcTokenValid(token);

  return (
    <ThemeProvider>
      <AntdConfigProvider>
        <BrowserRouter>
          <Toaster />
          <RequestErrorNotifier />
          <PermissionDeniedModal />
          <Routes>
            <Route
              path="/login/*"
              element={hasValidToken ? <Navigate to="/dashboard" replace /> : <OidcLoginRedirect />}
            />
            <Route
              path="/saml/*"
              element={hasValidToken ? <Navigate to="/dashboard" replace /> : <OidcLoginRedirect />}
            />
            <Route
              path="/home"
              element={hasValidToken ? <Navigate to="/dashboard" replace /> : <OidcLoginRedirect />}
            />
            <Route path="/feishu/callback" element={<FeishuCallback />} />
            <Route path="/" element={<OidcCallback />} />
            <Route
              path="/dashboard"
              element={
                <AuthGuard>
                  <Layout>
                    <Dashboard />
                  </Layout>
                </AuthGuard>
              }
            />
            <Route
              path="/agents"
              element={
                <AuthGuard>
                  <Layout>
                    <Agents />
                  </Layout>
                </AuthGuard>
              }
            />
            <Route
              path="/agent-templates"
              element={
                <AuthGuard>
                  <Layout>
                    <Agents />
                  </Layout>
                </AuthGuard>
              }
            />
            <Route
              path="/agents/create"
              element={
                <AuthGuard>
                  <Layout>
                    <CreateAgent />
                  </Layout>
                </AuthGuard>
              }
            />
            <Route
              path="/agents/:uid/edit"
              element={
                <AuthGuard>
                  <Layout>
                    <EditAgent />
                  </Layout>
                </AuthGuard>
              }
            />
            <Route
              path="/agents/:uid/create-instance"
              element={
                <AuthGuard>
                  <Layout>
                    <CreateAgentInstance />
                  </Layout>
                </AuthGuard>
              }
            />
            <Route
              path="/agents/:id"
              element={
                <AuthGuard>
                  <Layout>
                    <AgentOverview />
                  </Layout>
                </AuthGuard>
              }
            />
            <Route
              path="/agents/:id/apps"
              element={
                <AuthGuard>
                  <Layout>
                    <AgentApps />
                  </Layout>
                </AuthGuard>
              }
            />
            <Route
              path="/agents/:id/apps/:appId"
              element={
                <AuthGuard>
                  <Layout>
                    <AgentAppDetail />
                  </Layout>
                </AuthGuard>
              }
            />
            <Route
              path="/agents/:id/trace"
              element={
                <AuthGuard>
                  <Layout>
                    <AgentTrace />
                  </Layout>
                </AuthGuard>
              }
            />
            <Route
              path="/agents/:id/stats"
              element={
                <AuthGuard>
                  <Layout>
                    <AgentStats />
                  </Layout>
                </AuthGuard>
              }
            />
            <Route
              path="/applications"
              element={
                <AuthGuard>
                  <Layout>
                    <Applications />
                  </Layout>
                </AuthGuard>
              }
            />
            <Route
              path="/applications/create"
              element={
                <AuthGuard>
                  <Layout>
                    <CreateApplication />
                  </Layout>
                </AuthGuard>
              }
            />
            <Route
              path="/applications/:id/edit"
              element={
                <AuthGuard>
                  <Layout>
                    <EditApplication />
                  </Layout>
                </AuthGuard>
              }
            />
            <Route
              path="/channels"
              element={
                <AuthGuard>
                  <Layout>
                    <Channels />
                  </Layout>
                </AuthGuard>
              }
            />
            <Route
              path="/channel-accounts"
              element={
                <AuthGuard>
                  <Layout>
                    <ChannelAccounts />
                  </Layout>
                </AuthGuard>
              }
            />
            <Route
              path="/channel-accounts/:accountId"
              element={
                <AuthGuard>
                  <Layout>
                    <ChannelAccountDetailRouter />
                  </Layout>
                </AuthGuard>
              }
            />
            <Route
              path="/channels/:channelId/accounts"
              element={
                <AuthGuard>
                  <Layout>
                    <ChannelAccounts />
                  </Layout>
                </AuthGuard>
              }
            />
            <Route
              path="/channel-sessions"
              element={
                <AuthGuard>
                  <Layout>
                    <ChannelSessions />
                  </Layout>
                </AuthGuard>
              }
            />
            <Route
              path="/channels/:channelId/accounts/:accountId/sessions"
              element={
                <AuthGuard>
                  <Layout>
                    <ChannelSessions />
                  </Layout>
                </AuthGuard>
              }
            />
            <Route
              path="/settings"
              element={
                <AuthGuard>
                  <Layout>
                    <Settings />
                  </Layout>
                </AuthGuard>
              }
            />
            <Route
              path="/credentials"
              element={
                <AuthGuard>
                  <Layout>
                    <Credentials />
                  </Layout>
                </AuthGuard>
              }
            />
            <Route
              path="/users"
              element={
                <AuthGuard>
                  <Layout>
                    <Users />
                  </Layout>
                </AuthGuard>
              }
            />
            <Route
              path="/iam/users/*"
              element={
                <AuthGuard>
                  <Layout>
                    <IamUsersRoutes />
                  </Layout>
                </AuthGuard>
              }
            />
            <Route
              path="/iam/groups/*"
              element={
                <AuthGuard>
                  <Layout>
                    <IamGroupsRoutes />
                  </Layout>
                </AuthGuard>
              }
            />
            <Route
              path="/iam/policies/*"
              element={
                <AuthGuard>
                  <Layout>
                    <IamPoliciesRoutes />
                  </Layout>
                </AuthGuard>
              }
            />
            <Route
              path="/iam/grants/*"
              element={
                <AuthGuard>
                  <Layout>
                    <IamGrantsRoutes />
                  </Layout>
                </AuthGuard>
              }
            />
            <Route
              path="/plugins/mcps"
              element={
                <AuthGuard>
                  <Layout>
                    <MCPs />
                  </Layout>
                </AuthGuard>
              }
            />
            <Route
              path="/plugins/mcps/classes/new"
              element={
                <AuthGuard>
                  <Layout>
                    <CreateMCPClass />
                  </Layout>
                </AuthGuard>
              }
            />
            <Route
              path="/plugins/mcps/classes/:id"
              element={
                <AuthGuard>
                  <Layout>
                    <MCPClassDetail />
                  </Layout>
                </AuthGuard>
              }
            />
            <Route
              path="/plugins/mcps/classes/:id/edit"
              element={
                <AuthGuard>
                  <Layout>
                    <EditMCPClass />
                  </Layout>
                </AuthGuard>
              }
            />
            <Route
              path="/plugins/mcps/classes/:id/instances/new"
              element={
                <AuthGuard>
                  <Layout>
                    <CreateMCPInstanceFromClass />
                  </Layout>
                </AuthGuard>
              }
            />
            <Route
              path="/plugins/mcps/instances/new"
              element={
                <AuthGuard>
                  <Layout>
                    <CreateMCPInstance />
                  </Layout>
                </AuthGuard>
              }
            />
            <Route
              path="/plugins/mcps/instances/:id"
              element={
                <AuthGuard>
                  <Layout>
                    <MCPInstanceDetail />
                  </Layout>
                </AuthGuard>
              }
            />
            <Route
              path="/plugins/mcps/instances/:id/edit"
              element={
                <AuthGuard>
                  <Layout>
                    <EditMCPInstance />
                  </Layout>
                </AuthGuard>
              }
            />
            <Route
              path="/plugins/memories"
              element={
                <AuthGuard>
                  <Layout>
                    <MemoryCenter />
                  </Layout>
                </AuthGuard>
              }
            />
            <Route
              path="/plugins/memories/classes/:uid"
              element={
                <AuthGuard>
                  <Layout>
                    <MemoryClassDetail />
                  </Layout>
                </AuthGuard>
              }
            />
            <Route path="/plugins/rags" element={<Navigate to="/plugins/rags/build" replace />} />
            <Route
              path="/plugins/rags/collections"
              element={<Navigate to="/plugins/rags/build" replace />}
            />
            <Route
              path="/plugins/rags/build"
              element={
                <AuthGuard>
                  <Layout>
                    <RAGs />
                  </Layout>
                </AuthGuard>
              }
            />
            <Route
              path="/plugins/rags/management"
              element={<Navigate to="/plugins/rags/build" replace />}
            />
            <Route
              path="/plugins/rags/catalog"
              element={
                <AuthGuard>
                  <Layout>
                    <RAGCatalog />
                  </Layout>
                </AuthGuard>
              }
            />
            <Route
              path="/plugins/rags/group-classes/:id"
              element={
                <AuthGuard>
                  <Layout>
                    <RAGClassDetail />
                  </Layout>
                </AuthGuard>
              }
            />
            <Route
              path="/plugins/rags/groups/:id"
              element={
                <AuthGuard>
                  <Layout>
                    <RAGDetail />
                  </Layout>
                </AuthGuard>
              }
            />
            <Route
              path="/plugins/sandboxes"
              element={
                <AuthGuard>
                  <Layout>
                    <Sandboxes />
                  </Layout>
                </AuthGuard>
              }
            />
            <Route
              path="/plugins/sandboxes/create-class"
              element={
                <AuthGuard>
                  <Layout>
                    <CreateSandboxClass />
                  </Layout>
                </AuthGuard>
              }
            />
            <Route
              path="/plugins/sandboxes/classes/:uid"
              element={
                <AuthGuard>
                  <Layout>
                    <SandboxClassDetail />
                  </Layout>
                </AuthGuard>
              }
            />
            <Route
              path="/plugins/sandboxes/classes/:id/edit"
              element={
                <AuthGuard>
                  <Layout>
                    <EditSandboxClass />
                  </Layout>
                </AuthGuard>
              }
            />
            <Route
              path="/plugins/sandboxes/create-instance"
              element={
                <AuthGuard>
                  <Layout>
                    <CreateSandboxInstance />
                  </Layout>
                </AuthGuard>
              }
            />
            <Route
              path="/plugins/sandboxes/create-instance-from-class/:uid"
              element={
                <AuthGuard>
                  <Layout>
                    <CreateSandboxInstanceFromClass />
                  </Layout>
                </AuthGuard>
              }
            />
            <Route
              path="/plugins/sandboxes/instances/:id"
              element={
                <AuthGuard>
                  <Layout>
                    <SandboxInstanceDetail />
                  </Layout>
                </AuthGuard>
              }
            />
            <Route
              path="/plugins/sandboxes/instances/:id/edit"
              element={
                <AuthGuard>
                  <Layout>
                    <EditSandboxInstance />
                  </Layout>
                </AuthGuard>
              }
            />
            <Route
              path="/plugins/skills"
              element={
                <AuthGuard>
                  <Layout>
                    <Skills />
                  </Layout>
                </AuthGuard>
              }
            />
            <Route
              path="/plugins/skills/group-classes/:uid"
              element={
                <AuthGuard>
                  <Layout>
                    <SkillGroupClassDetail />
                  </Layout>
                </AuthGuard>
              }
            />
            <Route
              path="/plugins/skills/group-templates/:uid"
              element={
                <AuthGuard>
                  <Layout>
                    <SkillGroupDetail />
                  </Layout>
                </AuthGuard>
              }
            />
            <Route
              path="/plugins/skills/groups/:uid"
              element={
                <AuthGuard>
                  <Layout>
                    <SkillGroupDetail />
                  </Layout>
                </AuthGuard>
              }
            />
            <Route
              path="/plugins/skills/management"
              element={
                <AuthGuard>
                  <Layout>
                    <SkillsManagement />
                  </Layout>
                </AuthGuard>
              }
            />
            <Route
              path="/plugins/skills/management/:uid"
              element={
                <AuthGuard>
                  <Layout>
                    <SkillManagementDetail />
                  </Layout>
                </AuthGuard>
              }
            />
            <Route
              path="/plugins/skills/plaza"
              element={
                <AuthGuard>
                  <Layout>
                    <SkillsPlaza />
                  </Layout>
                </AuthGuard>
              }
            />
            <Route
              path="/plugins/skills/plaza/:uid"
              element={
                <AuthGuard>
                  <Layout>
                    <SkillsPlazaDetail />
                  </Layout>
                </AuthGuard>
              }
            />
            <Route
              path="/plugins/models"
              element={
                <AuthGuard>
                  <Layout>
                    <Models />
                  </Layout>
                </AuthGuard>
              }
            />
            <Route path="/skills" element={<Navigate to="/plugins/skills" replace />} />
            <Route path="/skills/classes/:id" element={<LegacySkillClassDetailRedirect />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </BrowserRouter>
      </AntdConfigProvider>
    </ThemeProvider>
  );
}

export default App;
